import {
  DivPaymentDetailRawData,
  TimeSeriesDataType,
  NullableDividendData,
  DividendData,
} from '../hooks/types';
import { isNil, compact, uniq, uniqBy } from '@/utilities/fp';

export const isValidDividend = (
  dividend: NullableDividendData
): dividend is DividendData => {
  if (isNil(dividend.exDate) || isNil(dividend.divAmount)) {
    return false;
  }
  const date = new Date(dividend.exDate);
  return date.toString() !== 'Invalid Date';
};

export const getValidDividends = (
  rawData: DivPaymentDetailRawData
): DividendData[] => compact(rawData?.dividends?.filter(isValidDividend));

export const sumSameDayDividends = (dividends: DividendData[]) => {
  return dividends.map((dividend) => {
    const sameDayDividends = dividends.filter(
      (div) => div.exDate === dividend.exDate
    );
    const totalDivAmount = sameDayDividends.reduce(
      (acc, curr) => acc + curr.divAmount,
      0
    );
    const sameDayFreqs = uniq(
      sameDayDividends.map((sameDayDividend) => sameDayDividend.divFreqTypeName)
    );

    return {
      ...dividend,
      divFreqTypeName: sameDayFreqs.join(', '),
      divAmount: totalDivAmount,
    };
  });
};

export const normalizeDividends = (dividends: DividendData[]) => {
  const sameDayDividends = sumSameDayDividends(dividends);
  return uniqBy((dividend: DividendData) => {
    return dividend.exDate;
  })(sameDayDividends);
};

export const mapTimeSeriesData =
  (rate: number) =>
  (dividends: DividendData[]): TimeSeriesDataType =>
    dividends.map((dividend) => {
      const time = new Date(dividend.exDate).getTime();
      const value = dividend.divAmount / rate;
      return [time, value];
    });

export const mapDivAmount = (rate: number) => (dividends: DividendData[]) =>
  dividends.map((dividend) => {
    return dividend.divAmount / rate;
  });

export const getDividendSeries = (
  rawData: DivPaymentDetailRawData,
  rate: number
): TimeSeriesDataType => {
  const validDividends = getValidDividends(rawData);
  const normalizedDividends = normalizeDividends(validDividends);
  return mapTimeSeriesData(rate)(normalizedDividends);
};

export const getYieldSeries = (
  rawData: DivPaymentDetailRawData
): TimeSeriesDataType => {
  const validDividends = getValidDividends(rawData);
  const normalizedDividends = normalizeDividends(validDividends);
  const yieldData: TimeSeriesDataType = normalizedDividends.map((dividend) => [
    new Date(dividend.exDate).getTime(),
    dividend.annualizedYield / 100,
  ]);
  return yieldData;
};

export const getDPSSeries = (
  rawData: DivPaymentDetailRawData
): TimeSeriesDataType => {
  const validDividends = getValidDividends(rawData);
  const normalizedDividends = normalizeDividends(validDividends);
  const dpsData: TimeSeriesDataType = normalizedDividends.map((dividend) => [
    new Date(dividend.exDate).getTime(),
    dividend.annualizedDividend,
  ]);
  return dpsData;
};

export const getMinMax = (
  dataSeries: TimeSeriesDataType,
  max: number,
  min: number
) => {
  const dataValues = dataSeries.map((values) => values[1]);

  return {
    max: Math.max(...dataValues, max),
    min: Math.min(...dataValues, min),
  };
};

export const getCurrencyISOCode = (
  rawData: DivPaymentDetailRawData | undefined
) => rawData?.dividends?.[0]?.currencyIsoCode || '';

export const getFrequency = (rawData: DivPaymentDetailRawData) =>
  rawData?.dividends?.[0]?.divFreqTypeName?.toLowerCase() || '';
